import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [errors, setErrors] = useState({});
  const [serverErrors, setServerErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tokenStatus, setTokenStatus] = useState("checking");
  const [userType, setUserType] = useState(null);
  const [completed, setCompleted] = useState(false);

  const passwordRegex = useMemo(
    () =>
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!%*?&#^]{8,}$/,
    []
  );

  useEffect(() => {
    if (!token) {
      setTokenStatus("invalid");
      return;
    }

    let active = true;

    const validateToken = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/auth/users/password-token/validate`,
          { token }
        );

        if (!active) return;

        setUserType(res.data.data.userType);
        setTokenStatus("valid");
      } catch {
        if (!active) return;
        setTokenStatus("invalid");
      }
    };

    validateToken();

    return () => {
      active = false;
    };
  }, [token]);

  const passwordChecks = useMemo(
    () => [
      {
        label: "At least 8 characters",
        valid: password.length >= 8,
      },
      {
        label: "One uppercase letter",
        valid: /[A-Z]/.test(password),
      },
      {
        label: "One lowercase letter",
        valid: /[a-z]/.test(password),
      },
      {
        label: "One number",
        valid: /\d/.test(password),
      },
      {
        label: "One special character",
        valid: /[@$!%*?&#^]/.test(password),
      },
    ],
    [password]
  );

  const validate = () => {
    const newErrors = {};

    if (!password) {
      newErrors.password = "Password is required";
    } else if (!passwordRegex.test(password)) {
      newErrors.password =
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    }

    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setErrors({});
    setServerErrors([]);

    if (!validate()) return;

    setLoading(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/auth/users/set-password`,
        { token, password, confirmPassword }
      );

      if (userType === "TECHNICIAN") {
        setCompleted(true);
      } else if (userType === "ADMIN") {
        setCompleted(true);
        setTimeout(() => navigate("/login"), 1800);
      } else {
        setCompleted(true);
      }
    } catch (err) {
      if (err.response?.data?.errors) {
        setServerErrors(err.response.data.errors);
      } else {
        setServerErrors([
          { field: "general", message: "Something went wrong. Try again." },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full rounded-2xl border px-4 py-3 pr-12 text-sm outline-none transition placeholder:text-slate-400 focus:ring-4 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500";

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center justify-center">
        <div className="grid w-full overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.10)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_20px_60px_rgba(0,0,0,0.35)] lg:grid-cols-[1.05fr_0.95fr]">
          {/* Left panel */}
          <div className="relative hidden overflow-hidden lg:block">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.12),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.08),transparent_28%)]" />

            <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-12">
              <div className="inline-flex w-fit items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80 backdrop-blur-md">
                Secure Password Setup
              </div>

              <div className="max-w-xl">
                <h1 className="text-4xl font-semibold leading-tight tracking-tight text-white">
                  Complete your account setup securely.
                </h1>

                <p className="mt-4 text-sm leading-7 text-white/75">
                  Create a strong password to activate secure access for your
                  account. This step protects platform access for administrators
                  and operational users.
                </p>

                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-md">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
                      Security
                    </p>
                    <p className="mt-2 text-sm font-medium text-white">
                      Strong credential policy
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-md">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
                      Access
                    </p>
                    <p className="mt-2 text-sm font-medium text-white">
                      Protected account activation
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-md">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">
                      Control
                    </p>
                    <p className="mt-2 text-sm font-medium text-white">
                      Verified password reset flow
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-xs text-white/55">
                @2026 Agri Fundi E.A. - All Rights Reserved
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="relative flex items-center justify-center bg-white px-6 py-8 dark:bg-slate-900 sm:px-8 lg:px-10">
            <div className="w-full max-w-md">
              <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_18px_50px_rgba(0,0,0,0.32)]">
                {tokenStatus === "checking" && (
                  <div className="flex flex-col items-center justify-center py-14 text-center">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-700 dark:border-t-slate-200" />
                    <h2 className="mt-5 text-xl font-semibold text-slate-900 dark:text-white">
                      Validating secure link
                    </h2>
                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                      Please wait while we verify your password setup token.
                    </p>
                  </div>
                )}

                {tokenStatus === "invalid" && (
                  <div className="py-8 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 text-2xl dark:bg-red-500/10">
                      ⚠️
                    </div>
                    <h2 className="mt-5 text-2xl font-semibold text-red-700 dark:text-red-300">
                      Invalid or Expired Link
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                      This password setup link is invalid or has expired. Please
                      contact your administrator to request a new secure link.
                    </p>
                  </div>
                )}

                {tokenStatus === "valid" && completed && userType === "TECHNICIAN" && (
                  <div className="py-10 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-2xl dark:bg-emerald-500/10">
                      ✅
                    </div>
                    <h2 className="mt-5 text-2xl font-semibold text-emerald-700 dark:text-emerald-300">
                      Password Set Successfully
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                      Your account is ready. Please sign in using the{" "}
                      <span className="font-semibold text-slate-900 dark:text-white">
                        AgriFundi mobile app
                      </span>
                      .
                    </p>
                  </div>
                )}

                {tokenStatus === "valid" &&
                  completed &&
                  userType === "ADMIN" && (
                    <div className="py-10 text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-2xl dark:bg-emerald-500/10">
                        ✅
                      </div>
                      <h2 className="mt-5 text-2xl font-semibold text-emerald-700 dark:text-emerald-300">
                        Password Created
                      </h2>
                      <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                        Redirecting you to the login page...
                      </p>
                    </div>
                  )}

                {tokenStatus === "valid" && !completed && (
                  <>
                    {loading && (
                      <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-[28px] bg-black/45 backdrop-blur-sm">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/80 border-t-transparent" />
                        <p className="mt-4 text-sm font-semibold text-white">
                          Setting password...
                        </p>
                      </div>
                    )}

                    <div className="mb-6 text-center">
                      <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                        Account Activation
                      </div>

                      <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                        Set Your Password
                      </h2>

                      <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                        Choose a strong password to complete account setup.
                      </p>
                    </div>

                    <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-950/30">
                      <p className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">
                        Password requirements
                      </p>

                      <div className="grid gap-2">
                        {passwordChecks.map((rule) => (
                          <div
                            key={rule.label}
                            className="flex items-center gap-2 text-sm"
                          >
                            <span
                              className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                                rule.valid
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                                  : "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                              }`}
                            >
                              {rule.valid ? "✓" : "•"}
                            </span>
                            <span
                              className={
                                rule.valid
                                  ? "text-slate-900 dark:text-white"
                                  : "text-slate-500 dark:text-slate-400"
                              }
                            >
                              {rule.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {serverErrors.length > 0 && (
                      <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                        {serverErrors.map((e, i) => (
                          <p key={i} className="text-sm">
                            • {e.message}
                          </p>
                        ))}
                      </div>
                    )}

                    <div className="space-y-5">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                          New Password
                        </label>

                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            className={`${inputBase} ${
                              errors.password
                                ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-100 dark:border-red-500/60 dark:bg-red-500/10 dark:focus:ring-red-500/10"
                                : "border-slate-300 bg-white focus:border-slate-900 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:focus:border-white dark:focus:ring-slate-800"
                            }`}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter new password"
                          />

                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                          >
                            {showPassword ? "Hide" : "Show"}
                          </button>
                        </div>

                        {errors.password && (
                          <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                            {errors.password}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Confirm Password
                        </label>

                        <div className="relative">
                          <input
                            type={showConfirm ? "text" : "password"}
                            className={`${inputBase} ${
                              errors.confirmPassword
                                ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-100 dark:border-red-500/60 dark:bg-red-500/10 dark:focus:ring-red-500/10"
                                : "border-slate-300 bg-white focus:border-slate-900 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:focus:border-white dark:focus:ring-slate-800"
                            }`}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-enter password"
                          />

                          <button
                            type="button"
                            onClick={() => setShowConfirm((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg px-2 py-1 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                          >
                            {showConfirm ? "Hide" : "Show"}
                          </button>
                        </div>

                        {errors.confirmPassword && (
                          <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>

                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-black"
                      >
                        Set Password
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}